import React, { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { Card, CardBody, Button, Input, Typography } from "@material-tailwind/react";
import { usePortalAuth } from "../context/PortalAuthContext";
import { portalApi } from "../services/portalApi";
import { selectClsLg } from "../constants/formClasses";
import PortalSelect from "../components/PortalSelect";
import { MEXICAN_STATES } from "../data/mexicanStates";
import { PortalShell } from "../components/PortalShell";
import EmployerAccountSelects from "../components/register/EmployerAccountSelects";

export default function PortalAdminNewClient() {
  const { user, isAuthenticated } = usePortalAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    state: "",
    companyEmployerId: "",
    companyAccountId: "",
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    password: "",
    passwordConfirm: "",
  });
  const [loading, setLoading] = useState(false);

  function setField(k, v) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  function setCompanySelection(sel) {
    setForm((f) => ({
      ...f,
      companyEmployerId: sel.employerId,
      companyAccountId: sel.accountId,
    }));
  }

  async function onSubmit(e) {
    e.preventDefault();
    if (form.password !== form.passwordConfirm) {
      toast.error("Las contraseñas no coinciden");
      return;
    }
    if (form.password.length < 8) {
      toast.error("La contraseña debe tener al menos 8 caracteres");
      return;
    }
    if (!form.companyEmployerId || !form.companyAccountId) {
      toast.error("Selecciona empresa y subempresa o cuenta");
      return;
    }
    setLoading(true);
    try {
      await portalApi.adminCreateClient({
        state: form.state,
        companyEmployerId: form.companyEmployerId,
        companyAccountId: form.companyAccountId,
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email,
        phone: form.phone,
        password: form.password,
        passwordConfirm: form.passwordConfirm,
      });
      toast.success("Usuario creado correctamente");
      navigate("/admin/clientes", { replace: true });
    } catch (err) {
      toast.error(err.message || "No se pudo crear el usuario");
    } finally {
      setLoading(false);
    }
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  if (user?.role !== "admin") {
    if (user?.role === "psychologist") {
      return <Navigate to="/psicologo" replace />;
    }
    return <Navigate to="/panel" replace />;
  }

  return (
    <PortalShell
      title="Nuevo usuario"
      subtitle="Registra un colaborador con acceso al portal de citas."
    >
      <div className="max-w-3xl">
        <Card className="border border-brand-brown/15 shadow-lg shadow-brand-brown/5">
          <CardBody className="p-6 sm:p-8">
            <form onSubmit={onSubmit} className="space-y-4" noValidate>
              <EmployerAccountSelects
                value={{
                  employerId: form.companyEmployerId,
                  accountId: form.companyAccountId,
                }}
                onChange={setCompanySelection}
                disabled={loading}
              />
              <div>
                <label className="block text-sm font-medium text-brand-dark mb-1.5">
                  Estado (México)
                </label>
                <PortalSelect size="lg"
                  className={selectClsLg}
                  value={form.state}
                  onChange={(e) => setField("state", e.target.value)}
                  required
                >
                  <option value="">Selecciona el estado</option>
                  {MEXICAN_STATES.map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </PortalSelect>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Input
                  label="Nombre(s)"
                  value={form.firstName}
                  onChange={(e) => setField("firstName", e.target.value)}
                  required
                  size="lg"
                  className="!text-brand-dark"
                />
                <Input
                  label="Apellidos"
                  value={form.lastName}
                  onChange={(e) => setField("lastName", e.target.value)}
                  required
                  size="lg"
                  className="!text-brand-dark"
                />
              </div>
              <Input
                type="email"
                label="Correo electrónico"
                value={form.email}
                onChange={(e) => setField("email", e.target.value)}
                autoComplete="email"
                required
                size="lg"
                className="!text-brand-dark"
              />
              <Input
                label="Teléfono"
                value={form.phone}
                onChange={(e) => setField("phone", e.target.value)}
                autoComplete="tel"
                required
                size="lg"
                className="!text-brand-dark"
              />
              <Input
                type="password"
                label="Contraseña (mín. 8 caracteres)"
                value={form.password}
                onChange={(e) => setField("password", e.target.value)}
                required
                minLength={8}
                size="lg"
                className="!text-brand-dark"
              />
              <Input
                type="password"
                label="Confirmar contraseña"
                value={form.passwordConfirm}
                onChange={(e) => setField("passwordConfirm", e.target.value)}
                required
                size="lg"
                className="!text-brand-dark"
              />
              <div className="flex flex-wrap gap-3 pt-2">
                <Button
                  type="submit"
                  size="lg"
                  className="bg-brand-lavender normal-case text-base shadow-md shadow-brand-lavender/25"
                  disabled={loading}
                >
                  {loading ? "Creando usuario…" : "Crear usuario"}
                </Button>
                <Link
                  to="/admin/clientes"
                  className="inline-flex items-center rounded-lg border border-brand-brown/30 px-5 py-2.5 text-sm font-semibold text-brand-dark hover:bg-brand-beige/50 transition-colors"
                >
                  Cancelar
                </Link>
              </div>
            </form>
            <Typography className="mt-6 text-sm text-brand-gray leading-relaxed">
              El usuario podrá iniciar sesión con el correo y la contraseña que definas aquí.
            </Typography>
          </CardBody>
        </Card>
      </div>
    </PortalShell>
  );
}
